import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, CheckCircle2, CircleDot, Circle, XCircle, Clock } from "lucide-react";

/**
 * Iter230 · LicitacionTimeline — Línea temporal de una licitación (CRM).
 * Se replica el estilo del Cliente 360º pero orientado al ciclo Publicada →
 * Estudio → Presentada → Apertura → Adjudicación → Firma → Inicio.
 * Consume GET /api/ofertas-comercial/{oid}/timeline.
 */

function Milestone({ ms, isLast }) {
  const state = ms.status || "pending";
  const level = ms.level;
  let Icon, iconCls, lineCls, textCls;
  if (level === "error") {
    Icon = XCircle;
    iconCls = "bg-rose-100 text-rose-700 border-rose-200";
    lineCls = "bg-rose-200";
    textCls = "text-rose-700";
  } else if (level === "success") {
    Icon = CheckCircle2;
    iconCls = "bg-emerald-100 text-emerald-700 border-emerald-200";
    lineCls = "bg-emerald-300";
    textCls = "text-emerald-700";
  } else if (state === "done") {
    Icon = CheckCircle2;
    iconCls = "bg-[#E8F5E9] text-[#007934] border-green-200";
    lineCls = "bg-[#B7DFC1]";
    textCls = "text-gray-900";
  } else if (state === "current") {
    Icon = CircleDot;
    iconCls = "bg-blue-100 text-blue-700 border-blue-200 ring-2 ring-blue-300 ring-offset-1";
    lineCls = "bg-blue-100";
    textCls = "text-blue-900";
  } else {
    Icon = Circle;
    iconCls = "bg-gray-50 text-gray-300 border-gray-200";
    lineCls = "bg-gray-100";
    textCls = "text-gray-500";
  }
  const fecha = (ms.at || "").slice(0, 10);

  return (
    <div className="flex gap-3" data-testid={`lic-timeline-milestone-${ms.key}`}>
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${iconCls}`}>
          <Icon size={14} />
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 ${lineCls}`} style={{ minHeight: 24 }} />}
      </div>
      <div className={`flex-1 pb-4 ${state === "pending" ? "opacity-60" : ""}`}>
        <div className="flex items-baseline justify-between gap-2">
          <p className={`text-sm font-semibold ${textCls}`}>{ms.label}</p>
          {fecha && <span className="text-[10px] text-gray-400 whitespace-nowrap">{fecha}</span>}
        </div>
        {ms.actor && <p className="text-[11px] text-gray-500">{ms.actor}</p>}
        {ms.note && <p className="text-[11px] text-gray-500 mt-0.5 italic">{ms.note}</p>}
      </div>
    </div>
  );
}

export default function LicitacionTimeline({ oid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!oid) { setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(`${API}/ofertas-comercial/${oid}/timeline`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "No se pudo cargar la línea temporal");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [oid]);

  if (!oid) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800" data-testid="lic-timeline-no-oid">
        Guarda la licitación primero para ver su línea temporal.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40" data-testid="lic-timeline-loading">
        <Loader2 size={20} className="animate-spin text-gray-300" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-xs text-rose-800" data-testid="lic-timeline-error">
        {error}
      </div>
    );
  }

  const milestones = data?.milestones || [];
  if (milestones.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-600 text-center" data-testid="lic-timeline-empty">
        Sin hitos registrados aún.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lic-timeline">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1.5"><Clock size={11}/> Línea temporal</p>
          <h3 className="text-base font-bold font-['Outfit'] mt-0.5">{data?.licitacion?.titulo || "Licitación"}</h3>
          <div className="flex items-center gap-2 text-[11px] opacity-80 mt-1">
            {data?.licitacion?.estado && <span className="bg-white/10 rounded px-2 py-0.5">{data.licitacion.estado}</span>}
            {data?.licitacion?.fase && <span className="bg-white/10 rounded px-2 py-0.5">{data.licitacion.fase}</span>}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        {milestones.map((ms, i) => (
          <Milestone key={ms.key} ms={ms} isLast={i === milestones.length - 1} />
        ))}
      </div>
    </div>
  );
}
